import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {CostRegistryResolver} from '@q9elements/ui-kit/common';

import {DecisionEngineWrapperComponent} from './decision-engine/components';
import {SalesforceConnectCallbackGuard} from './salesforce-orgs/guards/salesforce-connect-callback.guard';
import {SalesforceRefreshCallbackGuard} from './salesforce-orgs/guards/salesforce-refresh-callback.guard';
import {CommonWrapperComponent} from './shared/components';
import {AuthWrapperComponent} from './shared/components/auth-wrapper/auth-wrapper.component';
import {ActiveUserGuard} from './shared/guards/active-user.guard';
import {AuthGuard} from './shared/guards/auth.guard';
import {EditorGuard} from './shared/guards/de-editor.guard';
import {FirstLoginGuard} from './shared/guards/first-login.guard';
import {LicenseGuard} from './shared/guards/license.guard';
import {LogoutGuard} from './shared/guards/logout.guard';
import {OAuth2Guard} from './shared/guards/oauth2.guard';
import {QueryParamsGuard} from './shared/guards/query-params.guard';
import {RedirectGuard} from './shared/guards/redirect.guard';
import {SessionGuard} from './shared/guards/session.guard';
import {SpaceIndexValidateGuard} from './shared/guards/space-index-validate.guard';
import {ROUTES_PATH} from './shared/models/routes-path.enum';
import {UserEnterprisePermissionsResolver} from './shared/resolvers/user-enterprise-permissions.resolver';
import {AuthenticationRoutesMatcher, routes as authRoutes} from './shared/routing/auth.routes';
import {CommonRoutingMatcher, routes as commonRoutes} from './shared/routing/common.routes';
import {TASKS_TABS} from './tasks/models/tasks';

const routes: Routes = [
  {path: '', children: [], canActivate: [LogoutGuard], pathMatch: 'full'},
  {
    path: ROUTES_PATH.SF_REFRESH_CALLBACK,
    canActivate: [SalesforceRefreshCallbackGuard],
    children: []
  },
  {
    path: ROUTES_PATH.SF_CONNECT_CALLBACK,
    canActivate: [SalesforceConnectCallbackGuard],
    children: []
  },
  {
    path: ROUTES_PATH.NOT_FOUND,
    loadComponent: () => import('./not-found/not-found.component').then(c => c.NotFoundComponent)
  },
  {
    matcher: AuthenticationRoutesMatcher,
    component: AuthWrapperComponent,
    canActivate: [RedirectGuard],
    canMatch: [ActiveUserGuard, OAuth2Guard, AuthGuard],
    children: authRoutes
  },
  {
    path: `t/:index/${ROUTES_PATH.DECISION_ENGINE}`,
    component: DecisionEngineWrapperComponent,
    canMatch: [AuthGuard, OAuth2Guard, SessionGuard],
    canActivate: [
      SpaceIndexValidateGuard,
      LicenseGuard(
        p => p.hasDecisionEngineLicense() && p.elementsGPTEnabledInSpace(),
        ROUTES_PATH.DASHBOARD
      ),
      EditorGuard(ROUTES_PATH.DASHBOARD)
    ],
    title: 'Decision Engine',
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./decision-engine/decision-engine.module').then(m => m.DecisionEngineModule)
      }
    ]
  },
  {
    matcher: CommonRoutingMatcher,
    component: CommonWrapperComponent,
    canActivate: [RedirectGuard],
    canMatch: [AuthGuard, OAuth2Guard, SessionGuard],
    resolve: {
      userEnterprisePermissions: UserEnterprisePermissionsResolver,
      actionCosts: CostRegistryResolver
    },
    children: [
      {
        path: 't/:index',
        pathMatch: 'prefix',
        canActivate: [SpaceIndexValidateGuard, FirstLoginGuard],
        children: commonRoutes
      },
      {
        path: ROUTES_PATH.TASKS,
        canActivate: [QueryParamsGuard('ref', Object.values(TASKS_TABS))],
        loadChildren: () => import('./tasks/tasks.module').then(m => m.TasksModule)
      }
    ]
  },
  {
    path: ROUTES_PATH.PROBLEMS_SELECT,
    canMatch: [AuthGuard, SessionGuard],
    loadComponent: () =>
      import('./authentication/components/problems-select/problems-select.component').then(
        c => c.ProblemsSelectComponent
      )
  },
  {
    path: ROUTES_PATH.DIAGRAM_EDITOR_REQUEST_ACCESS,
    canActivate: [RedirectGuard],
    canMatch: [AuthGuard, SessionGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./maps/components/diagram-request-access/diagram-request-access.component').then(
            m => m.DiagramRequestAccessComponent
          )
      }
    ]
  },
  {path: '**', redirectTo: ROUTES_PATH.NOT_FOUND}
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {enableTracing: false})],
  exports: [RouterModule]
})
export class AppRoutingModule {}
